import { apiClient } from './apiClient';
import { indexedDbService } from './indexedDbService';
import { offlineService } from './offlineService';

export interface Form {
  id: number;
  businessId: string;
  code: string;
  description: string;
  displayOrder: number;
  numberOfColumns: number;
  headerImageBase64?: string;
  pdfConfigurationJson?: string;
  headerConfigurationJson?: string;
  createdDate: string;
  modifiedDate?: string;
  isActive: boolean;
  isPrintable: boolean;
  formSections: FormSection[];
}

export interface FormSection {
  id: number;
  formId: number;
  code: string;
  description: string;
  columnNumber: number;
  displayOrder: number;
  createdDate: string;
  modifiedDate?: string;
  isActive: boolean;
  formFields: FormField[];
}

export interface FormField {
  id: number;
  formSectionId: number;
  code: string;
  description: string;
  displayOrder: number;
  columnNumber: number; // 0 = full width, 1 = left, 2 = right
  fieldType: FormFieldType | string; // API returns string enum names, TypeScript uses numeric enum
  formFieldDataTypeId?: number;
  info?: string;
  configurationJson?: string;
  isRequired: boolean;
  createdDate: string;
  modifiedDate?: string;
  isActive: boolean;
  formFieldDataType?: FormFieldDataType;
}

export interface FormFieldDataType {
  id: number;
  businessId: string;
  code: string;
  description: string;
  displayOrder: number;
  category: DataTypeCategory;
  createdDate: string;
  modifiedDate?: string;
  isActive: boolean;
  formFieldDataItems: FormFieldDataItem[];
}

export interface FormFieldDataItem {
  id: number;
  formFieldDataTypeId: number;
  code: string;
  description: string;
  displayOrder: number;
  createdDate: string;
  modifiedDate?: string;
  isActive: boolean;
}

export enum FormFieldType {
  String = 1,
  Multiline = 2,
  Bool = 3,
  Dimension = 4,
  Int = 5,
  Dropdown = 6,
  Date = 7,
  DateTime = 8,
  CheckboxList = 9,
  HorizontalRule = 10
}

export enum DataTypeCategory {
  Dropdown = 1,
  CheckboxList = 2
}

export interface FormResponse {
  id: number;
  formId: number;
  jobId?: string;
  customerId?: string;
  responseData: string; // JSON string
  submittedBy: string;
  submittedDate: string;
  isActive: boolean;
}

export interface CreateFormResponseDto {
  formId: number;
  jobId?: string;
  customerId?: string;
  responseData: string; // JSON string
}

export interface JobFormResponseDto {
  id: string;
  jobId: string;
  formFieldId: number;
  value: string;
  createdDate: string;
  modifiedDate?: string;
  createdById: string;
  modifiedById?: string;
  formField: FormField;
}

class FormService {
  async getForms(): Promise<Form[]> {
    try {
      const isOnline = offlineService.getOnlineStatus();
      
      if (isOnline) {
        console.log('[FormService] Loading forms from API');
        const response = await apiClient.get('/forms');
        return response.data;
      } else {
        console.log('[FormService] Loading forms from IndexedDB (offline mode)');
        return await indexedDbService.getForms();
      }
    } catch (error: any) {
      console.error('[FormService] API failed, trying IndexedDB fallback:', error);
      
      // Fallback to IndexedDB
      try {
        return await indexedDbService.getForms();
      } catch (indexedDbError: any) {
        console.error('[FormService] IndexedDB fallback failed:', indexedDbError);
        throw new Error('Failed to load forms from both API and offline storage');
      }
    }
  }

  async getForm(id: number): Promise<Form> {
    try {
      const isOnline = offlineService.getOnlineStatus();
      
      if (isOnline) {
        console.log('[FormService] Loading form from API');
        const response = await apiClient.get(`/forms/${id}`);
        return response.data;
      } else {
        console.log('[FormService] Loading form from IndexedDB (offline mode)');
        return await indexedDbService.getForm(id);
      }
    } catch (error: any) {
      console.error('[FormService] API failed, trying IndexedDB fallback:', error);
      
      // Fallback to IndexedDB
      try {
        return await indexedDbService.getForm(id);
      } catch (indexedDbError: any) {
        console.error('[FormService] IndexedDB fallback failed:', indexedDbError);
        throw new Error('Failed to load form from both API and offline storage');
      }
    }
  }

  async getFormResponses(formId: number, jobId?: string): Promise<FormResponse[]> {
    try {
      const isOnline = offlineService.getOnlineStatus();
      
      if (isOnline) {
        console.log('[FormService] Loading form responses from API');
        const params = new URLSearchParams();
        if (jobId) params.append('jobId', jobId);
        
        const response = await apiClient.get(`/forms/${formId}/responses?${params.toString()}`);
        return response.data;
      } else {
        console.log('[FormService] Loading form responses from IndexedDB (offline mode)');
        return await indexedDbService.getFormResponses(formId, jobId);
      }
    } catch (error: any) {
      console.error('[FormService] API failed, trying IndexedDB fallback:', error);
      
      // Fallback to IndexedDB
      try {
        return await indexedDbService.getFormResponses(formId, jobId);
      } catch (indexedDbError: any) {
        console.error('[FormService] IndexedDB fallback failed:', indexedDbError);
        throw new Error('Failed to load form responses from both API and offline storage');
      }
    }
  }

  async getJobFormResponses(jobId: string): Promise<JobFormResponseDto[]> {
    try {
      const isOnline = offlineService.getOnlineStatus();
      
      if (isOnline) {
        console.log('[FormService] Loading job form responses from API');
        const response = await apiClient.get(`/forms/jobs/${jobId}/responses`);
        return response.data;
      } else {
        console.log('[FormService] Loading job form responses from IndexedDB (offline mode)');
        // For offline, we'd need to implement this in IndexedDB service
        // For now, return empty array
        return [];
      }
    } catch (error: any) {
      console.error('[FormService] API failed, trying IndexedDB fallback:', error);
      
      // Fallback to IndexedDB
      try {
        // For offline, we'd need to implement this in IndexedDB service
        // For now, return empty array
        return [];
      } catch (indexedDbError: any) {
        console.error('[FormService] IndexedDB fallback failed:', indexedDbError);
        throw new Error('Failed to load job form responses from both API and offline storage');
      }
    }
  }

  // Transform backend format (array of field responses) to frontend format (object keyed by field code)
  transformJobFormResponsesToFormData(
    responses: JobFormResponseDto[],
    form: Form
  ): { [key: string]: any } {
    const formData: { [key: string]: any } = {};
    
    responses.forEach(response => {
      // Find the field by FormFieldId in the form structure
      let fieldCode: string | undefined;
      
      form.formSections?.forEach(section => {
        const field = section.formFields?.find(f => f.id === response.formFieldId);
        if (field) {
          fieldCode = field.code;
        }
      });
      
      if (fieldCode) {
        let value: any = response.value;
        
        // Try to parse JSON for checkbox lists and other complex types
        try {
          const parsed = JSON.parse(response.value);
          if (Array.isArray(parsed)) {
            value = parsed;
          } else {
            value = parsed;
          }
        } catch {
          // If parsing fails, use the string value as-is
          // For boolean fields, convert string to boolean
          if (response.value === 'true' || response.value === 'True') {
            value = true;
          } else if (response.value === 'false' || response.value === 'False') {
            value = false;
          }
        }
        
        formData[fieldCode] = value;
      }
    });
    
    return formData;
  }

  async submitFormResponse(
    form: Form,
    jobId: string,
    formData: { [key: string]: any }
  ): Promise<any> {
    try {
      const isOnline = offlineService.getOnlineStatus();
      
      if (isOnline) {
        console.log('[FormService] Submitting form response via API');
        
        // Transform form data into backend format: array of field responses
        // Need to map field codes to FormFieldIds
        const fieldResponses: Array<{ formFieldId: number; value: string }> = [];
        
        // Iterate through all sections and fields to find matching codes
        form.formSections?.forEach(section => {
          section.formFields?.forEach(field => {
            const fieldCode = field.code;
            if (formData.hasOwnProperty(fieldCode)) {
              const value = formData[fieldCode];
              // Convert value to string, handling arrays for checkbox lists
              const valueString = Array.isArray(value) 
                ? JSON.stringify(value) 
                : value !== null && value !== undefined 
                  ? String(value) 
                  : '';
              
              fieldResponses.push({
                formFieldId: field.id,
                value: valueString
              });
            }
          });
        });
        
        // Use the batch endpoint to save all responses at once
        const response = await apiClient.post(`/forms/jobs/${jobId}/responses/batch`, fieldResponses);
        return response.data;
      } else {
        console.log('[FormService] Storing form response in IndexedDB (offline mode)');
        // For offline, store in the old format
        const responseData = {
          formId: form.id,
          jobId,
          customerId: '',
          responseData: JSON.stringify(formData)
        };
        return await indexedDbService.createFormResponse(responseData);
      }
    } catch (error: any) {
      console.error('[FormService] API failed, trying IndexedDB fallback:', error);
      
      // Fallback to IndexedDB
      try {
        const responseData = {
          formId: form.id,
          jobId,
          customerId: '',
          responseData: JSON.stringify(formData)
        };
        return await indexedDbService.createFormResponse(responseData);
      } catch (indexedDbError: any) {
        console.error('[FormService] IndexedDB fallback failed:', indexedDbError);
        throw new Error('Failed to submit form response to both API and offline storage');
      }
    }
  }

  getFieldTypeDisplayName(fieldType: FormFieldType): string {
    const typeNames: { [key in FormFieldType]: string } = {
      [FormFieldType.String]: 'Text',
      [FormFieldType.Multiline]: 'Multiline Text',
      [FormFieldType.Bool]: 'Checkbox',
      [FormFieldType.Dimension]: 'Dimension',
      [FormFieldType.Int]: 'Number',
      [FormFieldType.Dropdown]: 'Dropdown',
      [FormFieldType.Date]: 'Date',
      [FormFieldType.DateTime]: 'Date & Time',
      [FormFieldType.CheckboxList]: 'Checkbox List',
      [FormFieldType.HorizontalRule]: 'Horizontal Rule'
    };
    return typeNames[fieldType] || 'Unknown';
  }

  parseConfigurationJson(configJson?: string): any {
    if (!configJson) return {};
    try {
      return JSON.parse(configJson);
    } catch (error) {
      console.error('Failed to parse configuration JSON:', error);
      return {};
    }
  }

  /**
   * Get a context-specific configuration value (web, pdf, or both/default)
   * Supports both legacy string format and new object format:
   * - Legacy: "left" (applies to both web and pdf)
   * - New: { web: "left", pdf: "right" } or { web: "left", pdf: "right", default: "center" }
   */
  getContextValue(config: any, property: string, context: 'web' | 'pdf'): any {
    const value = config?.[property];
    if (value === undefined || value === null) {
      return undefined;
    }

    // If it's an object with context-specific values
    if (typeof value === 'object' && !Array.isArray(value)) {
      // Try context-specific value first
      if (value[context] !== undefined) {
        return value[context];
      }
      // Fall back to default value
      if (value.default !== undefined) {
        return value.default;
      }
      // If no default and no context match, return undefined
      return undefined;
    }

    // Legacy format - applies to both contexts
    return value;
  }
}

export const formService = new FormService();
